import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtDate } from "@/lib/roles";
import { MessageSquare, Plus, Loader2 } from "lucide-react";

export default function Complaints() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ subject: "", description: "", category: "general" });
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/complaints").then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/admin/complaints", form);
      toast.success("Complaint submitted");
      setForm({ subject: "", description: "", category: "general" });
      setShowForm(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setSubmitting(false);
  };

  return (
    <div className="space-y-6" data-testid="complaints-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Support</div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold flex items-center gap-2"><MessageSquare className="w-6 h-6" /> Complaints</h1>
        </div>
        <Button className="bg-[#E8702A] hover:bg-[#d95f1c]" onClick={() => setShowForm(!showForm)} data-testid="btn-toggle-complaint-form">
          <Plus className="w-4 h-4 mr-1" /> New Complaint
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader><CardTitle className="text-base font-heading">Raise a complaint</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Subject</Label><Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="complaint-subject" /></div>
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="property_tax">Property Tax</SelectItem>
                    <SelectItem value="water">Water/Sewerage</SelectItem>
                    <SelectItem value="payment">Payment</SelectItem>
                    <SelectItem value="license">License</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Description</Label><Textarea required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="complaint-description" /></div>
              <Button type="submit" disabled={submitting} className="bg-[#0F2942]" data-testid="btn-submit-complaint">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit"}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
            <div className="divide-y">
              {items.map((c) => (
                <div key={c.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold">{c.subject}</div>
                      <div className="text-xs text-slate-500 mt-1">{c.ticket_id} · {c.category} · {fmtDate(c.created_at)}</div>
                    </div>
                    <Badge variant={c.status === "resolved" ? "default" : "secondary"} className="capitalize">{c.status}</Badge>
                  </div>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{c.description}</p>
                  {c.response && (
                    <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-800 rounded text-sm">
                      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Official response</div>
                      {c.response}
                    </div>
                  )}
                </div>
              ))}
              {items.length === 0 && <div className="p-10 text-center text-slate-500">No complaints yet.</div>}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
